// src/Pages/backup/useBackupLogic.ts
import { useCallback, useEffect, useState } from "react";
import { getMsg, getModal } from "../../utils/antdStatic";
import {
  backupApi,
  mensajeDeError,
  type EstimacionMedios,
  type RestoreExtras,
} from "../../services/backup.api";
import { hoyChile } from "../../utils/fechaChile";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { nombreBackupZip, textoRolesCambiados, type EstadoEstimacion } from "./backupUtils";

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function useBackupLogic() {
  const [tables, setTables] = useState<string[]>([]);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState<"incremental" | "restore">(
    "incremental",
  );
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [summary, setSummary] = useState<Record<string, any> | null>(null);
  // Lo que el backend informaba y la página descartaba: archivos del Volume
  // escritos, tablas omitidas y tablas vaciadas. Sin esto, un restore que no
  // recuperó ni una sola foto se anunciaba en verde como "completada".
  const [extras, setExtras] = useState<RestoreExtras | null>(null);

  // ¿El ZIP completo lleva fotos, videos y documentos? Se recuerda por usuario
  // entre recargas (docs/filtros-persistentes.md). El default es «sí»: es la copia
  // total y lo que el botón hacía antes de que esto fuera elegible.
  const [incluirMedios, setIncluirMedios] = useFiltroPersistido<boolean>(
    "backup.incluirMedios",
    true,
    { validar: (v) => typeof v === "boolean" },
  );
  // Peso estimado de los medios, para decidir antes de descargar.
  const [estimacion, setEstimacion] = useState<EstimacionMedios | null>(null);
  const [estadoEstimacion, setEstadoEstimacion] =
    useState<EstadoEstimacion>("cargando");

  const fetchEstimacion = useCallback(async () => {
    setEstadoEstimacion("cargando");
    try {
      const data = await backupApi.getEstimacion();
      setEstimacion(data);
      setEstadoEstimacion(data ? "lista" : "no_disponible");
    } catch {
      setEstimacion(null);
      setEstadoEstimacion("no_disponible");
    }
  }, []);

  useEffect(() => {
    fetchEstimacion();
  }, [fetchEstimacion]);

  const fetchTables = useCallback(async () => {
    try {
      setLoadingTables(true);
      const data = await backupApi.getTables();
      setTables(data);
    } catch (e: any) {
      getMsg()?.error(e.message);
    } finally {
      setLoadingTables(false);
    }
  }, []);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const exportAll = async () => {
    try {
      setExporting(true);
      // La elección se fija al apretar el botón: si el operador cambia el
      // interruptor mientras baja, el archivo y su nombre siguen siendo coherentes.
      const conMedios = incluirMedios;
      const blob = await backupApi.exportAll(conMedios);
      // Fecha CHILENA, no UTC: un backup descargado a las 22:00 se guardaba con
      // la fecha de mañana. El backend ya lo hacía bien y el frontend le
      // pisaba el nombre al renombrar el archivo.
      const date = hoyChile();
      downloadBlob(blob, nombreBackupZip(date, conMedios));
      getMsg()?.success(
        conMedios
          ? "Backup descargado correctamente"
          : "Backup sin medios descargado correctamente",
      );
    } catch (e: any) {
      getMsg()?.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const exportSelected = async () => {
    if (selectedTables.length === 0) {
      getMsg()?.warning("Selecciona al menos una tabla");
      return;
    }
    try {
      setExporting(true);
      for (const tabla of selectedTables) {
        const blob = await backupApi.exportTable(tabla);
        // Fecha CHILENA, no UTC: un backup descargado a las 22:00 se guardaba con
      // la fecha de mañana. El backend ya lo hacía bien y el frontend le
      // pisaba el nombre al renombrar el archivo.
      const date = hoyChile();
        downloadBlob(blob, `${tabla}-${date}.csv`);
      }
      getMsg()?.success("Tablas descargadas correctamente");
    } catch (e: any) {
      getMsg()?.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const exportSingle = async (tabla: string) => {
    try {
      const blob = await backupApi.exportTable(tabla);
      // Fecha CHILENA, no UTC: un backup descargado a las 22:00 se guardaba con
      // la fecha de mañana. El backend ya lo hacía bien y el frontend le
      // pisaba el nombre al renombrar el archivo.
      const date = hoyChile();
      downloadBlob(blob, `${tabla}-${date}.csv`);
      getMsg()?.success(`${tabla}.csv descargado`);
    } catch (e: any) {
      getMsg()?.error(e.message);
    }
  };

  /**
   * Lanza la restauración. Si el backend responde 409 `TABLAS_NO_INCLUIDAS`,
   * muestra la lista y reintenta con la confirmación explícita.
   *
   * Ese 409 aparece al restaurar un backup anterior a alguna tabla que hoy
   * existe: esas tablas no vienen en el ZIP, así que quedarían con datos
   * posteriores al respaldo apuntando a filas que ya no son las mismas. Es una
   * decisión que tiene que tomar una persona mirando los nombres, no un
   * default.
   */
  const restaurar = async (confirmado = false): Promise<boolean> => {
    const res = await backupApi.restoreFull(uploadFiles, confirmado);

    if (res.status === 409 && res.data?.code === "TABLAS_NO_INCLUIDAS") {
      const tablas: string[] = res.data.noIncluidas ?? [];
      const modal = getModal();
      if (!modal) {
        getMsg()?.error(mensajeDeError(res.data, "Error al restaurar"));
        return false;
      }
      const acepta = await new Promise<boolean>((resolve) => {
        modal.confirm({
          title: "El backup es anterior a algunas tablas",
          width: 620,
          okText: "Vaciarlas y restaurar",
          cancelText: "Cancelar",
          okButtonProps: { danger: true },
          content: `Estas ${tablas.length} tabla(s) existen hoy y no vienen en el backup: ${tablas.join(", ")}. Si se dejan como están, quedan mezcladas con datos viejos y apuntando a registros que ya no existen. Al confirmar se vacían junto con el resto.`,
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!acepta) return false;
      return restaurar(true);
    }

    if (!res.result) {
      getMsg()?.error(mensajeDeError(res.data, "Error al restaurar"));
      return false;
    }

    setSummary(res.result.summary);
    setExtras(res.result);

    // El toast ya no es siempre verde. Un restore puede dejar la base perfecta
    // y no haber escrito ni un archivo del Volume (Volume sin montar, disco
    // lleno, ZIP de la era "solo tablas"): eso NO es una restauración
    // completada, y anunciarlo como tal hace que el operador borre el único ZIP
    // que tenía las fotos.
    // Un backup sin medios también es un aviso: la base volvió, pero las fotos,
    // videos y documentos que no estén ya en el volumen quedan rotos.
    // Cambiar el rol de cuentas que ya existían tampoco es un «todo bien»: quien restaura tiene que enterarse (B3d).
    const huboProblemas =
      (res.result.filesFailed ?? 0) !== 0 ||
      (res.result.skipped?.length ?? 0) > 0 ||
      res.result.sinMedios === true ||
      textoRolesCambiados(res.result.usuariosCambiaronDeRol) !== null;
    if (huboProblemas) {
      getMsg()?.warning("Restauración terminada CON AVISOS: revisa el detalle");
    } else {
      getMsg()?.success("Restauración completada");
    }
    return true;
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      getMsg()?.warning("Selecciona archivos para cargar");
      return;
    }
    try {
      setUploading(true);
      setSummary(null);
      setExtras(null);
      if (uploadMode === "incremental") {
        const result = await backupApi.importIncremental(uploadFiles);
        setSummary(result.summary);
        setExtras({
          skipped: result.skipped,
          usuariosCambiaronDeRol: result.usuariosCambiaronDeRol,
          superadministradores: result.superadministradores,
        });
        if (textoRolesCambiados(result.usuariosCambiaronDeRol) !== null) {
          getMsg()?.warning("Importación terminada CON AVISOS: revisa el detalle");
        } else {
          getMsg()?.success("Importación completada");
        }
        setUploadFiles([]);
        return;
      }
      const ok = await restaurar();
      if (ok) setUploadFiles([]);
    } catch (e: any) {
      getMsg()?.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  return {
    tables,
    selectedTables,
    setSelectedTables,
    loadingTables,
    exporting,
    uploading,
    uploadMode,
    setUploadMode,
    uploadFiles,
    setUploadFiles,
    summary,
    setSummary,
    extras,
    incluirMedios,
    setIncluirMedios,
    estimacion,
    estadoEstimacion,
    exportAll,
    exportSelected,
    exportSingle,
    handleUpload,
  };
}
